import { Button } from 'primereact/button';
import { Card } from 'primereact/card';

import { InputText } from 'primereact/inputtext';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Toast } from 'primereact/toast';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Chip } from 'primereact/chip';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ReactNode } from 'react';
import {
  dashboardWidgetsAtom,
  selectWidgetAIAtom,
  portfolioIdAtom,
  currentDashboardIdAtom,
  agentChatInjectionAtom,
  agentChatPrefillAtom,
  agentChatReasoningContextAtom,
} from '~/atoms/dashboardAtom';
import { usePopout } from '~/contexts/PopoutContext';
import { useDashboard } from '~/hooks/useDashboard';
import '~/styles/streamChat.css';
import { useStreamChat } from './hooks/useStreamChat';
import { LoadingDots } from './LoadingDots';
import { MessageActions } from './MessageActions';
import { StreamChatProps } from './types';

// Custom styles for markdown rendering in chat
const chatMarkdownComponents: Components = {
  table: ({ children, ...props }) => (
    <div className="overflow-x-auto my-3">
      <table
        {...props}
        className="text-sm border-collapse"
        style={{
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--bq-border-muted)',
          minWidth: 'max-content',
        }}
      >
        {children as ReactNode}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead {...props} style={{ backgroundColor: 'var(--bq-surface-muted)' }}>
      {children as ReactNode}
    </thead>
  ),
  tbody: ({ children, ...props }) => (
    <tbody {...props}>{children as ReactNode}</tbody>
  ),
  tr: ({ children, ...props }) => (
    <tr {...props} style={{ borderBottom: '1px solid var(--bq-border-muted)' }}>
      {children as ReactNode}
    </tr>
  ),
  th: ({ children, ...props }) => (
    <th
      {...props}
      className="text-left font-semibold text-sm whitespace-nowrap"
      style={{
        color: 'var(--bq-text-primary)',
        borderRight: '1px solid var(--bq-border-muted)',
        padding: '0.75rem 1rem',
      }}
    >
      {children as ReactNode}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td
      {...props}
      className="text-sm"
      style={{
        color: 'var(--bq-text-secondary)',
        borderRight: '1px solid var(--bq-border-muted)',
        padding: '0.75rem 1rem',
        whiteSpace: 'nowrap',
      }}
    >
      {children as ReactNode}
    </td>
  ),
  ul: ({ children, ...props }) => (
    <ul {...props} className="list-disc list-inside my-1 pl-2">
      {children as ReactNode}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol {...props} className="list-decimal list-inside my-1 pl-2">
      {children as ReactNode}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li {...props} className="mb-1">
      {children as ReactNode}
    </li>
  ),
};

export const StreamChat = ({
  initialMessage = 'If you have any specific questions or need further analysis, feel free to ask!',
  onExpandToggle,
  dashboardId: dashboardIdProp,
}: StreamChatProps) => {
  // Get dashboardId from atom (set by DashboardPage) or from props
  const dashboardIdFromAtom = useAtomValue(currentDashboardIdAtom);
  const dashboardId = dashboardIdProp ?? dashboardIdFromAtom ?? undefined;
  const portfolioId = useAtomValue(portfolioIdAtom);
  const {
    messages,
    isProcessing,
    streamChatResponse,
    initializeChat,
    isInitialized,
    appendAgentMessage,
    clearMessages,
  } = useStreamChat({ dashboardId, portfolioId: portfolioId || undefined });
  const [pendingAgentMessage, setPendingAgentMessage] = useAtom(
    agentChatInjectionAtom,
  );
  const [prefillMessage, setPrefillMessage] = useAtom(agentChatPrefillAtom);
  const reasoningContext = useAtomValue(agentChatReasoningContextAtom);
  const setReasoningContext = useSetAtom(agentChatReasoningContextAtom);
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (pendingAgentMessage?.content) {
      appendAgentMessage(pendingAgentMessage.content);
      setPendingAgentMessage(null);
    }
  }, [
    isInitialized,
    appendAgentMessage,
    pendingAgentMessage,
    setPendingAgentMessage,
  ]);

  useEffect(() => {
    if (!isInitialized) return;

    if (prefillMessage) {
      setInput(prefillMessage);
      setPrefillMessage(null);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isInitialized, prefillMessage, setPrefillMessage]);

  const [input, setInput] = useState('');
  const scrollPanelRef = useRef<ScrollPanel>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const toastRef = useRef<Toast>(null);
  const shouldScrollRef = useRef(true);

  const dashboardWidgets = useAtomValue(dashboardWidgetsAtom);
  const { data: dashboardData } = useDashboard(dashboardId);

  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);
  const { openPopout } = usePopout();

  const handleChatPopout = useCallback(() => {
    openPopout(
      'chat-ai',
      {
        type: 'StreamChat',
        props: {
          initialMessage,
          onExpandToggle: () => undefined,
          dashboardId,
        },
      },
      {
        width: 500,
        height: 700,
        title: 'Banqora AI Assistant',
      },
    );
  }, [initialMessage, openPopout, dashboardId]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const id = e.dataTransfer.getData('text/plain');
      if (id) {
        setSelectWidgetAI(id);
      }
    },
    [setSelectWidgetAI],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Gather all selected widgetIds
  const selectedWidgetIds = useMemo(
    () =>
      Object.entries(dashboardWidgets)
        .filter(([, state]) => state.aiButtonClicked)
        .map(([id]) => id),
    [dashboardWidgets],
  );

  // Map to configs and titles
  const selectedWidgetConfigs = useMemo(
    () =>
      selectedWidgetIds
        .map((id) => dashboardData?.config?.find((w) => w.id === id))
        .filter(Boolean) as NonNullable<typeof dashboardData>['config'],
    [dashboardData?.config, selectedWidgetIds],
  );

  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!hasInitializedRef.current) {
      initializeChat(initialMessage);
      hasInitializedRef.current = true;
    }
  }, [initialMessage, initializeChat]);

  const scrollToBottom = useCallback(() => {
    const element = scrollPanelRef.current?.getElement();
    if (!element) return;

    const contentDiv = element.querySelector('[data-pc-section="content"]');
    if (contentDiv) {
      contentDiv.scrollTo({
        top: contentDiv.scrollHeight,
        behavior: 'smooth',
      });
      shouldScrollRef.current = false;
    }
  }, []);

  const handleSendMessage = useCallback(() => {
    if (!input.trim() || isProcessing) return;

    // Send user's message as-is for display, but include reasoning context in API call
    streamChatResponse(input.trim(), selectedWidgetIds, reasoningContext)
      .catch((error) => {
        console.error('Error in chat response:', error);
        if (toastRef.current) {
          toastRef.current.show({
            severity: 'error',
            summary: 'Connection Error',
            detail: 'Failed to connect to the chat service',
            life: 5000,
          });
        }
      })
      .finally(() => {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      });

    setInput('');
  }, [
    input,
    isProcessing,
    streamChatResponse,
    selectedWidgetIds,
    reasoningContext,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  useEffect(() => {
    shouldScrollRef.current = true;
  }, [messages]);

  useEffect(() => {
    if (shouldScrollRef.current) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  const handleClearMessages = useCallback(() => {
    clearMessages(initialMessage);
    // Clear reasoning context when chat is cleared
    setReasoningContext(null);
  }, [clearMessages, initialMessage, setReasoningContext]);

  const renderChatHeader = (
    <div className="chat-header flex justify-content-between align-items-center p-2 sticky-header">
      <div className="flex align-items-center">
        <img
          src="/logo.svg"
          alt="Banqora Logo"
          className="mr-2"
          style={{ width: '1.2rem', height: '1.2rem' }}
        />
        <span className="font-medium text-sm">Banqora AI Assistant</span>
      </div>
      <div className="flex">
        {/* <Button
          icon="pi pi-plus"
          text
          rounded
          className="p-button-sm p-button-text mr-1"
          style={{ padding: '0.15rem' }}
          pt={{ icon: { style: { fontSize: '0.8rem' } } }}
          disabled
          title="New chat (coming soon)"
        /> */}
        <Button
          icon="pi pi-trash"
          text
          rounded
          className="p-button-sm p-button-text mr-1"
          style={{ padding: '0.15rem' }}
          pt={{ icon: { style: { fontSize: '0.8rem' } } }}
          onClick={handleClearMessages}
          title="Clear chat history"
        />
        <Button
          icon="pi pi-external-link"
          text
          rounded
          className="p-button-sm p-button-text mr-1"
          style={{ padding: '0.15rem' }}
          pt={{ icon: { style: { fontSize: '0.8rem' } } }}
          onClick={handleChatPopout}
          title="Pop out chat"
        />
        <Button
          icon="pi pi-chevron-left"
          text
          rounded
          className="p-button-sm p-button-text"
          style={{ padding: '0.15rem' }}
          pt={{ icon: { style: { fontSize: '0.8rem' } } }}
          onClick={onExpandToggle}
        />
      </div>
    </div>
  );

  const renderChatInput = (
    <div className="chat-input">
      {selectedWidgetConfigs.length > 0 && (
        <div
          style={{
            marginBottom: '0.5rem',
            display: 'flex',
            gap: '0.25rem',
            flexWrap: 'wrap',
          }}
        >
          {selectedWidgetConfigs.map((cfg) => {
            const title = cfg.shortDescription || cfg.id;
            return (
              <Chip
                key={cfg.id}
                label={title}
                style={{ fontWeight: 500, fontSize: '0.75rem' }}
                removable
                onRemove={() => setSelectWidgetAI(cfg.id)}
              />
            );
          })}
        </div>
      )}
      <div
        className="p-inputgroup"
        style={selectedWidgetIds.length > 0 ? { marginTop: 0 } : {}}
      >
        <InputText
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question"
          disabled={isProcessing}
          className="w-full text-sm p-2 dark-input"
          // style={{
          //   // borderRadius: '4px',
          //   // marginTop: selectedWidgetIds.length > 0 ? 0 : undefined,
          // }}
          ref={inputRef}
          aria-label="Chat message input"
        />
        <Button
          icon="pi pi-send"
          onClick={handleSendMessage}
          disabled={!input.trim() || isProcessing}
          className="p-button-sm p-inputgroup-addon"
          // style={{
          //   // borderRadius: '4px',
          //   // backgroundColor: '#555555',
          //   // borderColor: '#555555',
          // }}
          aria-label="Send message"
        />
      </div>
    </div>
  );

  return (
    <div
      className="flex flex-column chat-container"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <Toast ref={toastRef} position="top-right" />

      {renderChatHeader}

      <div className="chat-main-content flex flex-column">
        <ScrollPanel
          ref={scrollPanelRef}
          className="chat-messages-area"
          pt={{ content: { className: 'dark-scrollpanel-content' } }}
        >
          <div className="p-2">
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`mb-2 ml-1 ${message.isUser ? 'text-right' : 'text-left'}`}
              >
                <Card
                  className={`inline-block shadow-none ${
                    message.isUser && index === 1
                      ? 'user-message-first'
                      : message.isUser
                        ? 'user-message'
                        : 'bot-message'
                  }`}
                  style={{
                    maxWidth: '85%',
                    minWidth: '180px',
                    // borderRadius: '8px',
                    fontSize: '0.875rem',
                    position: 'relative',
                  }}
                  pt={{
                    root: { className: 'p-0 dark-card-root' },
                    body: {
                      className: 'p-2',
                      style: {
                        borderRadius: '6px',
                      },
                    },
                    content: { className: 'p-0' },
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <div className="message-content">
                      <Markdown
                        remarkPlugins={[remarkGfm]}
                        components={chatMarkdownComponents}
                      >
                        {message.content}
                      </Markdown>
                      {message.isStreaming && message.content.length < 10 && (
                        <LoadingDots />
                      )}
                    </div>
                    {!message.isUser &&
                      message.content &&
                      message.id !== '1' && (
                        <MessageActions
                          messageContent={message.content}
                          citations={message.citations}
                        />
                      )}
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </ScrollPanel>

        {renderChatInput}
      </div>
    </div>
  );
};
